import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { AuthContext } from "../../context/AuthContext";
import { ADMIN_API_URL, isAffiliateTestEnvironment } from "../../utils/env";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import {
  ScenarioCommand,
  ScenarioOperation,
  ScenarioSummary,
  ScenarioView,
  scenarioID,
} from "./ScenarioTypes";

export function useScenarioAPI(selected: string) {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const allowed = isAffiliateTestEnvironment() && Boolean(tokenId);
  const endpoint = ADMIN_API_URL("affiliates/scenarios");
  const list = useSWR<{ testnet_only: true; scenarios: ScenarioSummary[] }>(
    allowed ? [endpoint, tokenId] : null,
    async () => (await axios.get(endpoint, queryConfig())).data,
    { refreshInterval: 5000, shouldRetryOnError: false }
  );
  const view = useSWR<ScenarioView>(
    allowed && selected
      ? [`${endpoint}/${encodeURIComponent(selected)}`, tokenId]
      : null,
    async () =>
      (
        await axios.get(
          `${endpoint}/${encodeURIComponent(selected)}`,
          queryConfig()
        )
      ).data,
    { refreshInterval: 3000, shouldRetryOnError: false }
  );
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<string>();
  const [receipt, setReceipt] = useState<string>();
  const [retryCommand, setRetryCommand] = useState<ScenarioCommand>();
  const [pendingCommand, setPendingCommand] = useState<ScenarioCommand>();
  const verified = allowed && list.data?.testnet_only === true && !list.error;
  // Always read SWR data so its dependency tracking subscribes on the first render.
  const detail = view.data;
  const safeView =
    verified &&
    !view.error &&
    detail?.simulated === true &&
    detail?.real_funds_moved === false &&
    detail.id === selected;
  const awaitingApply =
    pendingCommand &&
    !(
      view.data?.id === pendingCommand.scenario_id &&
      view.data?.applied_request_ids?.includes(pendingCommand.request_id)
    )
      ? pendingCommand
      : undefined;
  useEffect(() => {
    if (pendingCommand && !awaitingApply) setPendingCommand(undefined);
  }, [pendingCommand, awaitingApply]);
  useEffect(() => {
    setReceipt(undefined);
    setError(undefined);
  }, [selected]);

  const refresh = async () => {
    await Promise.all([list.mutate(), view.mutate()]);
  };
  const send = async (command: ScenarioCommand) => {
    if (!verified)
      throw new Error("Sign in to a verified testnet scenario backend first.");
    if (busyRef.current)
      throw new Error("Wait for the current scenario request.");
    busyRef.current = true;
    setBusy(true);
    setError(undefined);
    setReceipt(undefined);
    let acknowledged = false;
    try {
      const response = await axios.post(
        `${endpoint}/command`,
        command,
        queryConfig()
      );
      if (
        response.data?.success !== true ||
        response.data?.request_id !== command.request_id ||
        (response.data?.scenario_id !== undefined &&
          response.data.scenario_id !== command.scenario_id)
      ) {
        throw new Error("Backend did not acknowledge this scenario request.");
      }
      acknowledged = true;
      setReceipt(command.request_id);
      setPendingCommand(command);
      setRetryCommand(undefined);
      try {
        await refresh();
      } catch (cause) {
        setError(
          `Command accepted; refreshing state failed. Refresh before continuing. ${affiliateError(
            cause
          )}`
        );
      }
      return command.scenario_id;
    } catch (cause) {
      const status = (cause as { response?: { status?: number } }).response
        ?.status;
      if (
        !acknowledged &&
        (!status || (status !== undefined && status >= 500))
      ) {
        setRetryCommand(command);
      } else setRetryCommand(undefined);
      setError(
        acknowledged
          ? `Command accepted; refreshing state failed. Refresh before continuing. ${affiliateError(
              cause
            )}`
          : affiliateError(cause)
      );
      throw cause;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  return {
    list: list.data?.scenarios || [],
    view: safeView ? detail : undefined,
    verified,
    busy,
    error:
      error ||
      (list.error && affiliateError(list.error)) ||
      (view.error && affiliateError(view.error)),
    receipt,
    retryCommand,
    pendingCommand: awaitingApply,
    refresh,
    retry: () => retryCommand && send(retryCommand),
    command: (
      id: string,
      operation: ScenarioOperation,
      payload: Record<string, unknown>,
      requestID?: string
    ) => {
      if (retryCommand)
        throw new Error(
          "Retry the unconfirmed scenario request before another change."
        );
      if (awaitingApply)
        throw new Error(
          "Wait until the accepted scenario command appears in backend state."
        );
      return send({
        request_id: requestID || `scenario-admin-${scenarioID()}`,
        scenario_id: id,
        operation,
        payload,
      });
    },
  };
}
